import { Component, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { AdminDeliveryTerm, AttributesService, DeliveryTermVariant } from '../../_services/attributes.service';
import { AlertService } from '../../_services/alert.service';
import { UserService } from '../../_services/users.service';
import { ConfirmDialogComponent } from '../../../Shared/_components/confirm-dialog/confirm-dialog.component';

type Filter = 'ALL' | 'USED' | 'UNUSED';
export type Kind = 'RANGE' | 'CALL' | 'OTHER';

// What a slug means at checkout: "3-5" is a working-day range it builds the
// delivery estimate from, "call" is call-to-arrange, anything else gets none.
export function slugKind(slug: string): Kind {
  if (/^\d+-\d+$/.test(slug)) return 'RANGE';
  return slug === 'call' ? 'CALL' : 'OTHER';
}

// List order: ranges by their days, then "call", then the rest by name.
export function deliveryTermOrder(a: { deliveryTermSlug: string; deliveryTermDesc: string }, b: { deliveryTermSlug: string; deliveryTermDesc: string }): number {
  const rank = (t: { deliveryTermSlug: string }) => {
    if (slugKind(t.deliveryTermSlug) === 'RANGE') {
      const [x, y] = t.deliveryTermSlug.split('-').map(Number);
      return x * 1000 + y;
    }
    return slugKind(t.deliveryTermSlug) === 'CALL' ? 1e7 : 2e7;
  };
  return rank(a) - rank(b) || a.deliveryTermDesc.localeCompare(b.deliveryTermDesc);
}

// Admin roles - the only ones who can delete a term (server-side too).
export function canDeleteDeliveryTerms(userInfo: any): boolean {
  const roles = String(userInfo?.userrole ?? '').split(',').map((r) => r.trim());
  return ['ADMIN', 'SALEADMIN', 'S-ADMIN'].some((r) => roles.includes(r));
}

// Delivery terms shown on each variant ("Estimated delivery is 3-5 working
// days"): a searchable list with how many variants use each and a view panel
// listing those variants. Creating and editing happen on their own page.
// Only terms no variant uses can be deleted, and only by admins.
@Component({
    selector: 'app-delivery-terms',
    imports: [CommonModule, FormsModule, RouterLink, MainLayoutComponent, MatIconModule, MatTooltipModule, MatProgressSpinnerModule],
    templateUrl: './delivery-terms.component.html',
    styles: [`
    .dt-fade { animation: dt-fade .18s ease-out; }
    .dt-slide { animation: dt-slide .24s cubic-bezier(.2,.8,.2,1); }
    @keyframes dt-fade { from { opacity: 0; } }
    @keyframes dt-slide { from { transform: translateX(100%); } }
  `]
})
export class DeliveryTermsComponent implements OnInit {
  private attributesService = inject(AttributesService);
  private alertService = inject(AlertService);
  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  readonly slugKind = slugKind;
  readonly Math = Math;
  readonly scaleTicks = [0, 0.25, 0.5, 0.75, 1];

  loading = true;
  loadError = false;
  terms: AdminDeliveryTerm[] = [];
  search = '';
  filter: Filter = 'ALL';

  // View panel
  selected: AdminDeliveryTerm | null = null;
  variants: DeliveryTermVariant[] = [];
  variantsLoading = false;
  variantSearch = '';

  readonly canDelete = canDeleteDeliveryTerms(this.userService.isLoggedIn());

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.selected = null;
  }

  ngOnInit(): void {
    this.load(Number(this.route.snapshot.queryParamMap.get('selected')) || null);
  }

  // `openId`: the term to open in the panel once loaded (back from its edit page).
  load(openId: number | null = null): void {
    this.loading = true;
    this.loadError = false;
    this.attributesService.getAdminDeliveryTerms().subscribe({
      next: (rows) => {
        this.terms = rows;
        this.loading = false;
        if (this.selected) this.selected = rows.find((r) => r.id === this.selected!.id) ?? null;
        const open = openId ? rows.find((r) => r.id === openId) : null;
        if (open) this.view(open);
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  get counts() {
    const used = this.terms.filter((t) => t.variants > 0).length;
    return { total: this.terms.length, used, unused: this.terms.length - used };
  }

  // Ranges in day order, then call, then the rest.
  get visible(): AdminDeliveryTerm[] {
    const q = this.search.trim().toLowerCase();
    return this.terms
      .filter((t) => this.filter === 'ALL' || (this.filter === 'USED' ? t.variants > 0 : t.variants === 0))
      .filter((t) => !q || t.deliveryTermDesc.toLowerCase().includes(q) || t.deliveryTermSlug.toLowerCase().includes(q))
      .sort(deliveryTermOrder);
  }

  rangeOf(t: AdminDeliveryTerm): [number, number] | null {
    if (slugKind(t.deliveryTermSlug) !== 'RANGE') return null;
    const [a, b] = t.deliveryTermSlug.split('-').map(Number);
    return [a, b];
  }

  // The longest range sets the scale every row's day bar is drawn on.
  get maxDays(): number {
    return Math.max(10, ...this.terms.map((t) => this.rangeOf(t)?.[1] ?? 0));
  }

  get totalVariants(): number {
    return this.terms.reduce((n, t) => n + t.variants, 0);
  }

  share(t: AdminDeliveryTerm): number {
    return this.totalVariants ? Math.round((t.variants / this.totalVariants) * 100) : 0;
  }

  get groups() {
    const all = this.visible;
    return [
      { key: 'RANGE', title: 'Working-day ranges', hint: 'Checkout builds the delivery estimate from these', icon: 'date_range', items: all.filter((t) => slugKind(t.deliveryTermSlug) === 'RANGE') },
      { key: 'CALL', title: 'Call to arrange', hint: 'The customer is asked to call to arrange delivery', icon: 'call', items: all.filter((t) => slugKind(t.deliveryTermSlug) === 'CALL') },
      { key: 'OTHER', title: 'Other', hint: 'No delivery estimate at checkout', icon: 'storefront', items: all.filter((t) => slugKind(t.deliveryTermSlug) === 'OTHER') },
    ].filter((g) => g.items.length);
  }

  // ---- View ----
  view(term: AdminDeliveryTerm): void {
    if (this.selected?.id === term.id) {
      this.selected = null;
      return;
    }
    this.selected = term;
    this.variants = [];
    this.variantSearch = '';
    if (!term.variants) return;
    this.variantsLoading = true;
    this.attributesService.getDeliveryTermVariants(term.id).subscribe({
      next: (rows) => {
        this.variants = rows;
        this.variantsLoading = false;
      },
      error: () => {
        this.variantsLoading = false;
        this.alertService.alert('Could not load the variants on this term', 'error');
      },
    });
  }

  get visibleVariants(): DeliveryTermVariant[] {
    const q = this.variantSearch.trim().toLowerCase();
    if (!q) return this.variants;
    return this.variants.filter((v) => v.sku.toLowerCase().includes(q) || v.productName.toLowerCase().includes(q));
  }

  // ---- Create / edit (own page) ----
  openCreate(): void {
    this.router.navigate(['/delivery-terms/create']);
  }

  openEdit(term: AdminDeliveryTerm, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/delivery-terms/edit', term.id]);
  }

  // ---- Delete ----
  deleteTooltip(term: AdminDeliveryTerm): string {
    if (!this.canDelete) return 'Only admins can delete delivery terms';
    if (term.variants) return `Used by ${term.variants} variant${term.variants === 1 ? '' : 's'} - move them to another term first`;
    return 'Delete';
  }

  remove(term: AdminDeliveryTerm, event?: Event): void {
    event?.stopPropagation();
    if (!this.canDelete || term.variants) return;
    this.dialog
      .open(ConfirmDialogComponent, {
        data: { title: 'Delete delivery term', message: `Delete "${term.deliveryTermDesc}"? No variants use it.` },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result?.confirmation) return;
        this.attributesService.deleteDeliveryTerm(term.id).subscribe({
          next: () => {
            this.alertService.alert('Delivery term deleted', 'success');
            if (this.selected?.id === term.id) this.selected = null;
            this.load();
          },
          error: (error) => this.alertService.alert(error?.error?.message || 'Could not delete the delivery term', 'error'),
        });
      });
  }
}
